import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

const LOGO_URL = '/assets/hero/logo.png';

const SignUp = () => {
  const navigate = useNavigate();
  const { register, googleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('patient');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const userData = await register(email, password, name, role);
      toast.success('Welcome to Ourmend!');
      navigate(userData.role === 'healer' ? '/healer-dashboard' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex flex-col">
      <Helmet>
        <title>Sign Up | Ourmend</title>
        <meta name="description" content="Create your free Ourmend account to connect with trusted healers of color." />
      </Helmet>

      <nav className="bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center space-x-2 sm:space-x-3" data-testid="signup-logo-link">
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-9 w-9 sm:h-12 sm:w-12 object-contain" />
            <div className="text-left">
              <span className="text-lg sm:text-2xl font-bold" style={{ color: '#8B4513' }}>Ourmend</span>
              <p className="text-[10px] sm:text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </button>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="signup-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <main className="flex-1 px-4 sm:px-6 py-10 sm:py-16">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
              <UserPlus className="w-7 h-7 text-[#8B4513]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#3E2723' }}>Create your account</h1>
            <p className="text-base text-stone-700">Join the Ourmend community.</p>
          </div>

          <Card className="p-6 sm:p-8 bg-white shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="signup-name">Full Name</Label>
                <Input id="signup-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="signup-name-input" />
              </div>
              <div>
                <Label htmlFor="signup-email">Email</Label>
                <Input id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email-input" />
              </div>
              <div>
                <Label htmlFor="signup-password">Password</Label>
                <Input id="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="signup-password-input" />
              </div>
              <div>
                <Label htmlFor="signup-role">I am a</Label>
                <select
                  id="signup-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                  data-testid="signup-role-select"
                >
                  <option value="patient">Patient (looking for healers)</option>
                  <option value="healer">Healer (offering services)</option>
                </select>
                {role === 'healer' && (
                  <p className="text-xs text-stone-500 mt-1">
                    After signing up, you'll need to complete the healer application to be listed in our directory.
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#8B4513] hover:bg-[#6B3410] text-white font-medium"
                data-testid="signup-submit-btn"
              >
                {submitting ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or</span>
              </div>
            </div>

            <Button onClick={googleLogin} variant="outline" className="w-full" data-testid="signup-google-btn">
              Continue with Google
            </Button>

            <p className="text-sm text-center text-stone-600 mt-5">
              Already have an account?{' '}
              <button type="button" onClick={() => navigate('/')} className="text-[#8B4513] font-semibold hover:underline" data-testid="signup-signin-link">
                Sign in
              </button>
            </p>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SignUp;
